import { BusinessBusiness, SignalFreshnessStatus } from "../types";
import { normalizeTags } from "./tagUtils";
import { AudienceModeId } from "../config/audienceModes";
import { CANONICAL_SIGNALS, resolveCanonicalKey, SignalCategory, SignalConfidence, SignalDefinition } from "../config/signalRegistry";
import { analyzeBusinessProspect, getTagExplanation } from "./opportunityFinder";
import { calculateAudienceScore } from "./audienceScoring";
import { getOrCreateSignalFreshness, calculateSignalDecay } from "./signalFreshness";

export interface DetectedSignal {
  key: string;
  defaultLabel: string;
  label: string;
  category: SignalCategory;
  confidence: SignalConfidence;
  evidence: string;
  source: string;
  date: string;
  recency: string;
  value?: any;
  weight: number;
  filterable: boolean;
  rankingEligible: boolean;
  cardEligible: boolean;
  detailEligible: boolean;
  audienceDescription?: string;
  // Signal Freshness & Date Fields
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  last_checked_at?: string | null;
  last_updated_at?: string | null;
  source_updated_at?: string | null;
  detected_at?: string | null;
  status?: SignalFreshnessStatus;
  freshnessMultiplier?: number;
}

/**
 * Priority resolution for public source dates ONLY.
 * Returns null if the public source did not state a publication or modification date.
 * Never falls back to internal BizNearYou checked/found timestamps.
 */
export function getSignalDateISO(signal: {
  source_updated_at?: string | null;
  signal_source_updated_at?: string | null;
  source_published_at?: string | null;
  source_posted_at?: string | null;
  last_updated_at?: string | null;
} | null | undefined): string | null {
  if (!signal) return null;
  const candidate =
    signal.source_updated_at ||
    signal.signal_source_updated_at ||
    signal.source_published_at ||
    signal.source_posted_at ||
    signal.last_updated_at;

  if (!candidate || candidate === "Unknown" || candidate === "null" || candidate === "undefined") return null;
  const ms = new Date(candidate).getTime();
  if (isNaN(ms)) return null;
  return new Date(ms).toISOString();
}

/**
 * Returns timestamp ms for a public source date or null if not specified by source.
 */
export function getSignalUpdatedTimestamp(signal: any): number | null {
  const iso = getSignalDateISO(signal);
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return isNaN(ms) ? null : ms;
}

/**
 * Formats a signal date or ISO string strictly as M/D/YY (e.g. "7/29/26").
 * Returns null for invalid or missing inputs.
 */
export function formatCompactDate(value: string | Date | null | undefined): string | null {
  if (!value || value === "Unknown" || value === "null" || value === "undefined") return null;
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  }).format(date);
}

/**
 * Formats a public source date strictly as M/D/YY.
 * Returns "Date not specified by source" if missing or invalid. Never invents current date.
 */
export function formatSignalLastUpdated(signalDateISO?: string | null): string {
  const formatted = formatCompactDate(signalDateISO);
  if (!formatted) return "Date not specified by source";
  
  const ms = new Date(signalDateISO!).getTime();
  if (ms > Date.now() + 86400000) return "Date not specified by source";
  return formatted;
}

/**
 * Checks if a signal applies to the selected audience mode.
 */
export function signalAppliesToAudience(signal: DetectedSignal, audienceMode: AudienceModeId): boolean {
  const canonicalKey = resolveCanonicalKey(signal.key);
  const def = CANONICAL_SIGNALS[canonicalKey];
  if (def && def.audienceWeights && def.audienceWeights[audienceMode] !== undefined) {
    return def.audienceWeights[audienceMode] > 0;
  }
  return signal.weight > 0;
}

/**
 * Finds the newest relevant signal date for a business under the active audience mode.
 */
export function getLatestRelevantSignalDateISO(
  business: BusinessBusiness,
  audienceMode: AudienceModeId,
  distanceInMiles?: number | null,
  keyword: string = ""
): string | null {
  const normalized = normalizeExistingBusinessSignals(business, distanceInMiles, keyword, audienceMode);
  
  const relevantTimestamps = normalized
    .filter(signal => signalAppliesToAudience(signal, audienceMode))
    .map(signal => getSignalUpdatedTimestamp(signal))
    .filter((ts): ts is number => ts !== null && !isNaN(ts));

  if (relevantTimestamps.length > 0) {
    const maxTs = Math.max(...relevantTimestamps);
    return new Date(maxTs).toISOString();
  }

  // Fallback: check all normalized signals for this business
  const allTimestamps = normalized
    .map(signal => getSignalUpdatedTimestamp(signal))
    .filter((ts): ts is number => ts !== null && !isNaN(ts));

  if (allTimestamps.length > 0) {
    const maxTs = Math.max(...allTimestamps);
    return new Date(maxTs).toISOString();
  }

  return null;
}

/**
 * Normalizes all business signals into one shared, deduplicated collection.
 * Preserves every detected signal key with full freshness metadata.
 */
export function normalizeExistingBusinessSignals(
  business: BusinessBusiness,
  distanceInMiles?: number | null,
  keyword?: string,
  audienceMode: AudienceModeId = "job_seeker"
): DetectedSignal[] {
  const prospect = analyzeBusinessProspect(business, distanceInMiles);
  const audienceScoreResult = calculateAudienceScore(business, distanceInMiles ?? null, keyword || "", audienceMode);

  const rawTags = Array.from(
    new Set([
      ...normalizeTags(business.opportunityTags),
      ...normalizeTags(prospect.tags),
      ...normalizeTags(audienceScoreResult.reasons),
      ...normalizeTags(prospect.categorizedSignals?.workOpportunities),
      ...normalizeTags(prospect.categorizedSignals?.growthSignals),
      ...normalizeTags(prospect.categorizedSignals?.serviceOpportunities),
      ...normalizeTags(prospect.categorizedSignals?.contactSignals),
      ...normalizeTags(prospect.categorizedSignals?.warnings),
      ...normalizeTags(prospect.categorizedSignals?.aiAutomation)
    ])
  ).filter(Boolean);

  const signalMap = new Map<string, DetectedSignal>();

  for (const rawTag of rawTags) {
    const canonicalKey = resolveCanonicalKey(rawTag);
    const def: SignalDefinition = CANONICAL_SIGNALS[canonicalKey] || {
      key: canonicalKey,
      defaultLabel: rawTag,
      category: "Growth and Expansion",
      defaultSource: "System Analysis",
      defaultConfidence: "Basic",
      filterable: true,
      rankingEligible: true,
      cardEligible: true,
      detailEligible: true
    };

    const explanation = getTagExplanation(rawTag, business, distanceInMiles);
    const freshnessMeta = getOrCreateSignalFreshness(business, canonicalKey, explanation.source);
    const signalDateISO = getSignalDateISO(freshnessMeta);
    const decay = calculateSignalDecay(signalDateISO);

    // Skip expired signals (>60 days old) from active normalized collection
    if (decay.status === "expired" || decay.freshnessMultiplier <= 0) {
      continue;
    }

    const audienceLabel = def.audienceLabels?.[audienceMode] || def.defaultLabel;
    const audienceDesc = def.audienceDescriptions?.[audienceMode] || explanation.reason;
    const baseWeight = def.audienceWeights?.[audienceMode] ?? 5;
    const weight = Math.round(baseWeight * decay.freshnessMultiplier * 10) / 10;

    // If signal already registered, merge evidence and preserve the newest valid date
    if (signalMap.has(canonicalKey)) {
      const existing = signalMap.get(canonicalKey)!;
      if (!existing.evidence.includes(explanation.reason)) {
        existing.evidence += ` | ${explanation.reason}`;
      }
      if (explanation.confidence === "Full" && existing.confidence !== "Full") {
        existing.confidence = "Full";
      }

      // Keep the newest valid signal date when duplicate aliases are merged
      const existingTs = getSignalUpdatedTimestamp(existing);
      const newTs = getSignalUpdatedTimestamp(freshnessMeta);
      if (newTs !== null && (existingTs === null || newTs > existingTs)) {
        existing.first_seen_at = freshnessMeta.first_seen_at;
        existing.last_seen_at = freshnessMeta.last_seen_at;
        existing.last_updated_at = freshnessMeta.last_updated_at;
        existing.source_updated_at = freshnessMeta.source_updated_at;
        existing.detected_at = freshnessMeta.detected_at;
        existing.date = formatSignalLastUpdated(getSignalDateISO(existing));
      }
    } else {
      signalMap.set(canonicalKey, {
        key: canonicalKey,
        defaultLabel: def.defaultLabel,
        label: audienceLabel,
        category: def.category,
        confidence: explanation.confidence || def.defaultConfidence,
        evidence: explanation.reason || "Detected from business listing analysis",
        source: freshnessMeta.source || explanation.source || def.defaultSource,
        date: formatSignalLastUpdated(signalDateISO),
        recency: decay.status === "fresh" ? "Fresh" : decay.status === "aging" ? "Aging" : decay.status === "stale" ? "Stale" : "Unknown",
        weight,
        filterable: def.filterable,
        rankingEligible: def.rankingEligible,
        cardEligible: def.cardEligible,
        detailEligible: def.detailEligible,
        audienceDescription: audienceDesc,
        first_seen_at: freshnessMeta.first_seen_at,
        last_seen_at: freshnessMeta.last_seen_at,
        last_checked_at: freshnessMeta.last_checked_at,
        last_updated_at: freshnessMeta.last_updated_at,
        source_updated_at: freshnessMeta.source_updated_at,
        detected_at: freshnessMeta.detected_at,
        status: decay.status,
        freshnessMultiplier: decay.freshnessMultiplier
      });
    }
  }

  // 1. Phone Available Signal
  if (business.phone && !signalMap.has("phone_available")) {
    const def = CANONICAL_SIGNALS["phone_available"];
    signalMap.set("phone_available", {
      key: "phone_available",
      defaultLabel: "Phone Available",
      label: def.audienceLabels?.[audienceMode] || "Phone Available",
      category: "Contact and Access",
      confidence: "Full",
      evidence: `Direct telephone line available (${business.phone})`,
      source: "Business Listing",
      date: formatSignalLastUpdated(null),
      recency: "Active",
      weight: def.audienceWeights?.[audienceMode] ?? 8,
      filterable: true,
      rankingEligible: true,
      cardEligible: true,
      detailEligible: true,
      audienceDescription: "Direct phone line listed on business profile"
    });
  }

  // 2. Easy Application & Direct Contact Signal
  if ((business.phone || business.website || business.hiringPageUrl) && !signalMap.has("easy_application_direct_contact")) {
    const def = CANONICAL_SIGNALS["easy_application_direct_contact"];
    signalMap.set("easy_application_direct_contact", {
      key: "easy_application_direct_contact",
      defaultLabel: "Easy Application & Direct Contact",
      label: def.audienceLabels?.[audienceMode] || "Easy Application & Direct Contact",
      category: "Contact and Access",
      confidence: "Full",
      evidence: "Direct phone number, website, or application link on file for quick outreach",
      source: "Business Listing",
      date: formatSignalLastUpdated(null),
      recency: "Active",
      weight: def.audienceWeights?.[audienceMode] ?? 9,
      filterable: true,
      rankingEligible: true,
      cardEligible: true,
      detailEligible: true,
      audienceDescription: "Direct application link or phone line available for immediate contact"
    });
  }

  // 3. Rated X Signal
  if (business.rating && business.rating >= 3.8) {
    const key = "strong_reviews";
    const def = CANONICAL_SIGNALS[key];
    const ratingStr = `Rated ${business.rating.toFixed(1)} ★`;
    if (!signalMap.has(key)) {
      signalMap.set(key, {
        key,
        defaultLabel: ratingStr,
        label: ratingStr,
        category: def.category,
        confidence: "Full",
        evidence: `High customer satisfaction rating (${business.rating.toFixed(1)} stars across ${business.reviewsCount || 0} reviews)`,
        source: "Google Maps Listing",
        date: formatSignalLastUpdated(null),
        recency: "Active",
        weight: def.audienceWeights?.[audienceMode] ?? 8,
        filterable: true,
        rankingEligible: true,
        cardEligible: true,
        detailEligible: true,
        audienceDescription: `Customer rating of ${business.rating.toFixed(1)} stars from ${business.reviewsCount || 0} reviews`
      });
    } else {
      const existing = signalMap.get(key)!;
      existing.label = ratingStr;
    }
  }

  // 4. Distance Proximity Signal (e.g., 13.6 miles away)
  if (distanceInMiles !== null && distanceInMiles !== undefined) {
    const key = "distance_proximity";
    const def = CANONICAL_SIGNALS[key];
    const distStr = `${distanceInMiles.toFixed(1)} miles away`;
    if (!signalMap.has(key)) {
      signalMap.set(key, {
        key,
        defaultLabel: distStr,
        label: distStr,
        category: def.category,
        confidence: "Full",
        evidence: `Located ${distanceInMiles.toFixed(1)} miles from target search center`,
        source: "Geographic Analysis",
        date: formatSignalLastUpdated(null),
        recency: "Active",
        weight: def.audienceWeights?.[audienceMode] ?? 9,
        filterable: true,
        rankingEligible: true,
        cardEligible: true,
        detailEligible: true,
        audienceDescription: `Proximity of ${distanceInMiles.toFixed(1)} miles from your search location`
      });
    } else {
      const existing = signalMap.get(key)!;
      existing.label = distStr;
    }
  }

  // 5. Ensure contact availability signal is present if contact info exists
  if (business.contacts && business.contacts.length > 0) {
    const dmKey = "verified_decision_maker_found";
    if (!signalMap.has(dmKey)) {
      const def = CANONICAL_SIGNALS[dmKey];
      signalMap.set(dmKey, {
        key: dmKey,
        defaultLabel: def.defaultLabel,
        label: def.audienceLabels?.[audienceMode] || def.defaultLabel,
        category: def.category,
        confidence: "Full",
        evidence: `Direct executive contact found (${business.contacts[0]?.name || "Executive"}, ${business.contacts[0]?.role || "Decision Maker"})`,
        source: "Contact Search / Crawler",
        date: formatSignalLastUpdated(null),
        recency: "Active",
        weight: def.audienceWeights?.[audienceMode] ?? 8,
        filterable: true,
        rankingEligible: true,
        cardEligible: true,
        detailEligible: true,
        audienceDescription: def.audienceDescriptions?.[audienceMode] || "Verified direct contact info on file"
      });
    }
  }

  return Array.from(signalMap.values());
}

/**
 * Ranks a detected-signal collection for a specific audience mode without mutating the original array.
 */
export function rankSignalsForAudience(
  signals: DetectedSignal[],
  audienceMode: AudienceModeId
): DetectedSignal[] {
  return signals
    .map((sig) => {
      const def = CANONICAL_SIGNALS[sig.key];
      const audienceLabel = def?.audienceLabels?.[audienceMode] || sig.defaultLabel || sig.label;
      const audienceDescription = def?.audienceDescriptions?.[audienceMode] || sig.audienceDescription || sig.evidence;
      const weight = def?.audienceWeights?.[audienceMode] ?? sig.weight ?? 5;

      return {
        ...sig,
        label: audienceLabel,
        audienceDescription,
        weight
      };
    })
    .sort((a, b) => b.weight - a.weight);
}

/**
 * Returns Top Signals (4-6 prioritized signals for View Details & Cards)
 * WITHOUT mutating or destroying the underlying collection.
 */
export function getTopSignals(
  signals: DetectedSignal[],
  audienceMode: AudienceModeId,
  limit: number = 5
): DetectedSignal[] {
  const ranked = rankSignalsForAudience(signals, audienceMode);
  return ranked.slice(0, limit);
}

/**
 * Returns all remaining detected signals NOT included in Top Signals.
 */
export function getAdditionalSignals(
  signals: DetectedSignal[],
  audienceMode: AudienceModeId,
  topLimit: number = 5
): DetectedSignal[] {
  const ranked = rankSignalsForAudience(signals, audienceMode);
  return ranked.slice(topLimit);
}
